/**
 * access.ts (SETTINGS-ACCESS-01, 2026-09-03) — the data behind Settings →
 * People & Access: the roster (every active employee) joined to the login
 * (app_user), the login's live status from Auth (team-invite `status`), and
 * the admin actions on a login (invite, reset, temporary password, remove
 * authenticator, disable). Roles + per-user extras keep their storage
 * (app_role / app_user.grant_extra + deny); this file only reads them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { CAPABILITIES } from "@/lib/permissions/catalog";
import { deptRoleName } from "../deptRole";

/** ESC's holding-bucket pseudo employees (Parts, Follow Up Visit, Pending
 *  Installs, Serv Agr…) — columns on the board, not people who sign in. Same
 *  list the Team Directory uses. */
const PSEUDO_EMPNOS = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);
const PSEUDO_NAMES = /^(parts|follow ?up visit|pending installs?|serv ?agr|sales|unassigned|reschedule|off today|pick ?up part)/i;

export interface RosterEmployee {
  empno: string;
  empname: string | null;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  dept: string | null;
  skill: string | null;
  inactive: boolean | null;
}
export interface LoginRow {
  user_id: string;
  email: string | null;
  empno: string | null;
  role_key: string;
  grant_extra: string[] | null;
  deny: string[] | null;
}
export interface RoleRow {
  key: string;
  name: string;
  description: string | null;
  is_admin: boolean;
  capabilities: string[] | null;
}
export interface LoginStatus {
  user_id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  invited_at: string | null;
  confirmed: boolean;
  mfa: boolean;
  disabled: boolean;
}
export type LoginState = "none" | "invited" | "active" | "never" | "off";

/** One row on the People tab: an employee (or a login without one). */
export interface Person {
  key: string;                 // empno, or `login:<user_id>` for a login with no roster match
  empno: string | null;
  name: string;
  dept: string | null;
  skill: string | null;
  rosterEmail: string | null;
  login: LoginRow | null;
  status: LoginStatus | null;
  state: LoginState;
  role: RoleRow | null;
  extras: number;
}

export const ACCESS_STATUS_KEY = ["access_status"] as const;
export const ACCESS_EVENTS_KEY = ["access_events"] as const;

export function useRoles() {
  return useQuery({
    queryKey: ["app_roles"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<RoleRow[]> => {
      const { data, error } = await supabase.from("app_role").select("key, name, description, is_admin, capabilities")
        .order("is_admin", { ascending: false }).order("key");
      if (error) throw error;
      return (data ?? []) as RoleRow[];
    },
  });
}

export function useLogins() {
  return useQuery({
    queryKey: ["app_users"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<LoginRow[]> => {
      const { data, error } = await supabase.from("app_user").select("user_id, email, empno, role_key, grant_extra, deny").order("email");
      if (error) throw error;
      return (data ?? []) as LoginRow[];
    },
  });
}

/** Live login facts from Auth (last sign-in, two-factor, disabled) — admin only; others get an empty map. */
export function useAccessStatus(enabled = true) {
  return useQuery({
    queryKey: ACCESS_STATUS_KEY,
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Map<string, LoginStatus>> => {
      const { data, error } = await supabase.functions.invoke("team-invite", { body: { action: "status" } });
      if (error) return new Map();
      const r = data as { ok?: boolean; users?: LoginStatus[] } | null;
      return new Map((r?.users ?? []).map((u) => [u.user_id, u]));
    },
  });
}

export function useRoster() {
  return useQuery({
    queryKey: ["access_roster"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RosterEmployee[]> => {
      const { data, error } = await supabase.from("mirror_employee")
        .select("empno, empname, firstname, lastname, email, dept, skill, inactive")
        .order("lastname").order("firstname");
      if (error) throw error;
      return (data ?? []) as RosterEmployee[];
    },
  });
}

export function stateOf(login: LoginRow | null, status: LoginStatus | null): LoginState {
  if (!login) return "none";
  if (status?.disabled) return "off";
  if (status && !status.confirmed) return "invited";
  if (status && !status.last_sign_in_at) return "never";
  return "active";
}

export const STATE_META: Record<LoginState, { label: string; chip: string }> = {
  none: { label: "No login", chip: "bg-slate-100 text-slate-500" },
  invited: { label: "Invited", chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  never: { label: "Never Signed In", chip: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
  active: { label: "Active", chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  off: { label: "Disabled", chip: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
};

export function displayName(e: RosterEmployee | null, login: LoginRow | null): string {
  if (e) {
    const n = [e.firstname, e.lastname].filter(Boolean).join(" ").trim();
    return n || e.empname || e.empno;
  }
  const em = login?.email ?? "";
  return em.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Login";
}

/** Roster ⨝ logins ⨝ status → one list. Inactive employees only when they still hold a login. */
export function buildPeople(roster: RosterEmployee[], logins: LoginRow[], status: Map<string, LoginStatus>, roles: RoleRow[]): Person[] {
  const byEmpno = new Map<string, LoginRow>();
  for (const l of logins) if (l.empno) byEmpno.set(l.empno, l);
  const used = new Set<string>();
  const out: Person[] = [];
  for (const e of roster) {
    const login = byEmpno.get(e.empno) ?? null;
    if (e.inactive && !login) continue;
    if (!login && (PSEUDO_EMPNOS.has(e.empno) || PSEUDO_NAMES.test((e.empname ?? `${e.firstname ?? ""} ${e.lastname ?? ""}`).trim()))) continue;
    if (login) used.add(login.user_id);
    const st = login ? status.get(login.user_id) ?? null : null;
    out.push({
      key: e.empno, empno: e.empno, name: displayName(e, login), dept: deptRoleName(e.dept) ?? e.dept, skill: e.skill, rosterEmail: e.email,
      login, status: st, state: stateOf(login, st), role: login ? roles.find((r) => r.key === login.role_key) ?? null : null,
      extras: (login?.grant_extra?.length ?? 0) + (login?.deny?.length ?? 0),
    });
  }
  for (const l of logins) {
    if (used.has(l.user_id)) continue;
    const st = status.get(l.user_id) ?? null;
    out.push({
      key: `login:${l.user_id}`, empno: l.empno, name: displayName(null, l), dept: null, skill: null, rosterEmail: null,
      login: l, status: st, state: stateOf(l, st), role: roles.find((r) => r.key === l.role_key) ?? null,
      extras: (l.grant_extra?.length ?? 0) + (l.deny?.length ?? 0),
    });
  }
  // Logins first (they're the ones to manage), then the rest alphabetically.
  const rank = (p: Person) => (p.login ? 0 : 1);
  return out.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** Role caps + extras − never. Admin = everything. */
export function effectiveCapabilities(role: RoleRow | null, login: LoginRow | null): Set<string> {
  if (role?.is_admin) return new Set(CAPABILITIES.map((c) => c.key));
  const s = new Set<string>(role?.capabilities ?? []);
  for (const k of login?.grant_extra ?? []) s.add(k);
  for (const k of login?.deny ?? []) s.delete(k);
  return s;
}

export type AccessAction =
  | { action: "create_and_invite" | "resend_invite" | "send_reset"; email: string; empno?: string | null; role_key?: string }
  | { action: "create_with_password"; email: string; empno?: string | null; role_key?: string; password: string }
  | { action: "set_temp_password"; user_id: string; email?: string; password: string }
  | { action: "remove_mfa" | "disable_login" | "enable_login"; user_id: string; email?: string };

export interface AccessResult { ok?: boolean; blocked?: string; detail?: string; error?: string; sent?: string; removed?: number; user_id?: string | null; created?: boolean }

export function useAccessAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: AccessAction): Promise<AccessResult> => {
      const { data, error } = await supabase.functions.invoke("team-invite", { body: v });
      if (error) throw error;
      const r = (data ?? {}) as AccessResult;
      if (r.error) throw new Error(r.detail || r.error);
      return r;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACCESS_STATUS_KEY });
      qc.invalidateQueries({ queryKey: ["app_users"] });
      qc.invalidateQueries({ queryKey: ["app_users_link"] });
      qc.invalidateQueries({ queryKey: ACCESS_EVENTS_KEY });
    },
  });
}

export interface AccessEvent { id: string; action: string; actor_email: string | null; target_user_id: string | null; target_email: string | null; detail: string | null; created_at: string }
export function useAccessEvents(userId: string | null) {
  return useQuery({
    queryKey: [...ACCESS_EVENTS_KEY, userId],
    enabled: !!userId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AccessEvent[]> => {
      const { data, error } = await supabase.from("pegasus_access_event").select("*").eq("target_user_id", userId!)
        .order("created_at", { ascending: false }).limit(20);
      if (error) throw error;
      return (data ?? []) as AccessEvent[];
    },
  });
}

/** A temporary password a person can actually read back over a counter. */
export function suggestPassword(): string {
  const words = ["furnace", "copper", "eagan", "summer", "maple", "river", "boiler", "cedar", "prairie", "harbor", "lennox", "trapp"];
  const pick = () => words[Math.floor(Math.random() * words.length)];
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
  const n = Math.floor(100 + Math.random() * 900);
  return `${cap(pick())}-${pick()}-${n}`;
}
